import React, { useState } from "react";
import { User, UserForm, Role, Status } from "../../types";
import { useApp } from "../../context/AppContext";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Table, { Column } from "../../components/ui/Table";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import { Input, Select } from "../../components/ui/FormFields";
import { auth, db } from "../../firebase/config";
import {
  createUserWithEmailAndPassword,
  updatePassword,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail
} from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";

const BLANK: UserForm = { name: "", email: "", role: "candidate", orgId: "", status: "active" };

const UsersPage: React.FC = () => {
  const { users, orgs, addUser, updateUser, deleteUser, loading } = useApp();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [editUser, setEditUser] = useState<User | null>(null);
  const [deleteUserItem, setDeleteUserItem] = useState<User | null>(null);
  const [form, setForm] = useState<UserForm>(BLANK);
  const [saving, setSaving] = useState(false);
  const [passwordAction, setPasswordAction] = useState<"set" | "change" | "reset">("set");
  const [askSetPassword, setAskSetPassword] = useState<User | null>(null);
  const handleFormChange = (key: keyof UserForm) => (value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const openNew = () => {
    setEditUser(null);
    setForm(BLANK);
    setShowModal(true);
  };

  const openEdit = (user: User) => {
    setEditUser(user);
    setForm({
      name: user.name,
      email: user.email,
      role: user.role,
      orgId: user.orgId || "",
      status: user.status
    });
    setShowModal(true);
  };

  const openPasswordModal = (user: User, action: "set" | "change" | "reset") => {
    setSelectedUser(user);
    setPasswordAction(action);
    setPassword("");
    setConfirmPassword("");
    setShowPasswordModal(true);
  };

  const handlePasswordAction = async () => {
    if (passwordAction !== "reset" && password !== confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    if (passwordAction !== "reset" && password.length < 6) {
      alert("Password must be at least 6 characters long!");
      return;
    }

    setSaving(true);
    try {
      if (passwordAction === "set" && selectedUser) {
        // Create new user with password
        const userCredential = await createUserWithEmailAndPassword(auth, selectedUser.email, password);
        // Update Firestore with Firebase Auth UID
        const userRef = doc(db, "users", selectedUser.id);
        await updateDoc(userRef, {
          authUid: userCredential.user.uid,
          hasPassword: true
        });
        alert("User created successfully with password!");
      }
      else if (passwordAction === "change" && selectedUser) {
        // Need to re-authenticate or use admin SDK (this requires current user password)
        // For security, we'll send a password reset email instead
        await sendPasswordResetEmail(auth, selectedUser.email);
        alert(`Password reset email sent to ${selectedUser.email}. They can set a new password from the email link.`);
      }
      else if (passwordAction === "reset" && selectedUser) {
        // Send password reset email
        await sendPasswordResetEmail(auth, selectedUser.email);
        alert(`Password reset email sent to ${selectedUser.email}`);
      }

      setShowPasswordModal(false);
      setPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Password operation failed:", error);
      if (error.code === 'auth/email-already-in-use') {
        alert("This email is already registered. Please use the 'Reset Password' option instead.");
      } else if (error.code === 'auth/invalid-email') {
        alert("Invalid email address.");
      } else {
        alert(`Failed to ${passwordAction} password: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return;
    setSaving(true);
    try {
      if (editUser) {
        const updateData: Partial<User> = {
          name: form.name,
          email: form.email,
          role: form.role,
          status: form.status,
        };
        if (form.role !== "super_admin") {
          updateData.orgId = form.orgId || undefined;
        } else {
          updateData.orgId = undefined;
        }
        await updateUser(editUser.id, updateData);
        alert("User updated successfully!");
      } else {
        const now = new Date().toISOString();
        const userData: Omit<User, 'id'> = {
          name: form.name,
          email: form.email,
          role: form.role,
          status: form.status,
          createdAt: now,
        };
        if (form.role !== "super_admin") {
          userData.orgId = form.orgId || undefined;
        }
        const newUser = await addUser(userData);

        // Optionally prompt to set password
        if (newUser) {
          setAskSetPassword(newUser);
        }
      }
      setShowModal(false);
      setForm(BLANK);
    } catch (error) {
      console.error("Error saving user:", error);
      alert("Failed to save user. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  const handleDelete = async () => {
    if (deleteUserItem) {
      setSaving(true);
      try {
        // Send password reset email before deletion if needed
        await deleteUser(deleteUserItem.id);
        setDeleteUserItem(null);
        alert("User deleted successfully!");
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Failed to delete user. Please try again.");
      } finally {
        setSaving(false);
      }
    }
  };

  const formatDate = (date: any): string => {
    if (!date) return "";
    if (date?.toDate) return date.toDate().toLocaleDateString();
    if (typeof date === "string") return new Date(date).toLocaleDateString();
    if (date instanceof Date) return date.toLocaleDateString();
    return "";
  };

  const filtered = users.filter(user =>
    (roleFilter === "all" || user.role === roleFilter) &&
    (user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase()))
  );

  const cols: Column<User>[] = [
    {
      key: "name",
      label: "Name",
      render: (value, record) => (
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-300 to-purple-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {record.name?.charAt(0) || "?"}
          </div>
          <div>
            <p className="font-semibold text-gray-800 text-sm">{value}</p>
            <p className="text-xs text-gray-400">{record.email}</p>
          </div>
        </div>
      )
    },
    {
      key: "role",
      label: "Role",
      render: (value) => <Badge text={value as string} />
    },
    {
      key: "orgId",
      label: "Organization",
      render: (value) => {
        if (!value) return <span className="text-gray-300">—</span>;
        const org = orgs.find(o => o.id === value);
        return org?.name || value;
      }
    },
    {
      key: "status",
      label: "Status",
      render: (value) => <Badge text={value as string} />
    },
    {
      key: "hasPassword",
      label: "Password",
      render: (value, record) => (
        <div className="flex gap-2">
          <Badge text={value ? "Set" : "Not Set"} />
          <button
            onClick={() => openPasswordModal(record, value ? "reset" : "set")}
            className="text-xs text-indigo-600 hover:text-indigo-800 underline"
          >
            {value ? "Reset" : "Set Password"}
          </button>
        </div>
      )
    },
    { key: "createdAt", label: "Joined", render: (value) => formatDate(value) },
  ];

  if (loading) {
    return (
      <PageShell title="Users & Roles" subtitle="Manage platform users and permissions">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Users & Roles"
      subtitle="Manage platform users and permissions"
      action={{ label: "Add User", onClick: openNew }}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={users.length} icon="👥" color="#6366f1" />
        <StatCard label="Super Admins" value={users.filter(u => u.role === "super_admin").length} icon="🛡" color="#ec4899" />
        <StatCard label="Org Admins" value={users.filter(u => u.role === "org_admin").length} icon="🏢" color="#f59e0b" />
        <StatCard label="Candidates" value={users.filter(u => u.role === "candidate").length} icon="👤" color="#10b981" />
      </div>

      <GlassCard className="p-5">
        <div className="flex gap-3 mb-4 flex-wrap">
          <Input
            value={search}
            onChange={setSearch}
            placeholder="🔍 Search users…"
            className="flex-1 min-w-48"
          />
          <Select
            value={roleFilter}
            onChange={setRoleFilter}
            options={[
              { value: "all", label: "All Roles" },
              { value: "super_admin", label: "Super Admin" },
              { value: "org_admin", label: "Org Admin" },
              { value: "candidate", label: "Candidate" }
            ]}
          />
        </div>
        <Table
          columns={cols}
          data={filtered}
          onEdit={openEdit}
          onDelete={(user) => setDeleteUserItem(user)}
        />
      </GlassCard>

      {/* User Form Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editUser ? "Edit User" : "Add User"}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Full Name"
              value={form.name}
              onChange={handleFormChange("name")}
              required
            />
            <Input
              label="Email"
              value={form.email}
              onChange={handleFormChange("email")}
              type="email"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Role"
              value={form.role}
              onChange={(value) => setForm(prev => ({ ...prev, role: value as Role, orgId: value === "super_admin" ? "" : prev.orgId }))}
              options={[
                { value: "super_admin", label: "Super Admin" },
                { value: "org_admin", label: "Org Admin" },
                { value: "candidate", label: "Candidate" }
              ]}
            />
            <Select
              label="Status"
              value={form.status}
              onChange={(value) => setForm(prev => ({ ...prev, status: value as Status }))}
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" }
              ]}
            />
          </div>
          {form.role !== "super_admin" && (
            <Select
              label="Organization"
              value={form.orgId || ""}
              onChange={(value) => setForm(prev => ({ ...prev, orgId: value }))}
              options={[
                { value: "", label: "— Select —" },
                ...orgs.map(org => ({ value: org.id, label: org.name }))
              ]}
            />
          )}
          {!editUser && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                📝 After creating the user, you'll be able to set a password for them.
              </p>
            </div>
          )}
          <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving..." : (editUser ? "Update" : "Create")} User
            </Button>
          </div>
        </div>
      </Modal>

      {/* Password Management Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title={
          passwordAction === "set" ? "Set Password" :
            passwordAction === "change" ? "Change Password" :
              "Reset Password"
        }
      >
        <div className="space-y-4">
          <div className="bg-gray-50 p-3 rounded-lg mb-3">
            <p className="text-sm text-gray-600">User: <span className="font-semibold">{selectedUser?.name}</span></p>
            <p className="text-sm text-gray-600">Email: <span className="font-semibold">{selectedUser?.email}</span></p>
          </div>

          {passwordAction !== "reset" ? (
            <>
              <Input
                label="New Password"
                value={password}
                onChange={setPassword}
                type="password"
                required
                placeholder="Minimum 6 characters"
              />
              <Input
                label="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                type="password"
                required
                placeholder="Re-enter password"
              />
              <div className="bg-yellow-50 p-3 rounded-lg">
                <p className="text-xs text-yellow-800">
                  ⚠️ Password must be at least 6 characters long. Make sure to share the password securely with the user.
                </p>
              </div>
            </>
          ) : (
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800 mb-2">
                🔐 A password reset email will be sent to:
              </p>
              <p className="font-mono text-sm font-semibold">{selectedUser?.email}</p>
              <p className="text-xs text-blue-600 mt-3">
                The user will receive an email with a link to reset their password.
              </p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
            <Button onClick={handlePasswordAction} disabled={saving}>
              {saving ? "Processing..." : (
                passwordAction === "set" ? "Set Password" :
                  passwordAction === "change" ? "Change Password" :
                    "Send Reset Email"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteUserItem}
        onClose={() => setDeleteUserItem(null)}
        onConfirm={handleDelete}
        title="Delete User"
        message={`Remove "${deleteUserItem?.name}" from the platform? This will also remove their access.`}
      />
    </PageShell>
  );
};

export default UsersPage;